import React, { useState } from 'react';
import { Modal } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { AlertTriangle } from 'lucide-react';
import '../Common/ll-modal.css';
import './bank-import-warning.css';

const formatAmount = (amount) => Number(amount).toLocaleString('sv-SE', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
});

/**
 * Unresolved bank-history notice for one feed account.
 *
 * The account row only carries a quiet one-line indicator; the explanation,
 * the affected transactions and the two ways out (retry / CSV) live in a
 * dialog the user opens on purpose. The old inline yellow Alert put a wall
 * of amber text inside every affected row.
 */
export default function BankImportWarning({ account, companyId, onRetry, retryDisabled }) {
    const [show, setShow] = useState(false);
    const issues = account.import_issues;
    if (!issues && account.backfill_status !== 'failed') return null;
    const transactions = issues?.transactions || [];
    const count = issues?.count || 0;
    const accountLabel = account.clearing_number
        ? `${account.clearing_number}-${account.account_number}`
        : account.account_number || account.iban || '';

    const handleRetry = () => {
        // The sync reloads the whole settings card, so the dialog hands over
        // to the row spinner + result toast instead of lingering.
        setShow(false);
        onRetry();
    };

    return (
        <>
            <div className="bank-import-notice">
                <AlertTriangle size={14} className="bank-import-notice__icon" aria-hidden="true" />
                <span className="bank-import-notice__title">Historik behöver kontrolleras</span>
                {count > 0 && (
                    <span className="bank-import-notice__count">
                        {count} {count === 1 ? 'transaktion' : 'transaktioner'}
                    </span>
                )}
                <button type="button" className="bank-import-notice__action" onClick={() => setShow(true)}>
                    Visa detaljer
                </button>
            </div>

            <Modal show={show} onHide={() => setShow(false)} centered className="ll-modal bank-import-dialog">
                <Modal.Header closeButton>
                    <div>
                        <Modal.Title as="h5">Historik behöver kontrolleras</Modal.Title>
                        {accountLabel && (
                            <p className="ll-modal__subtitle">
                                Konto {accountLabel}{account.display_name ? ` · ${account.display_name}` : ''}
                            </p>
                        )}
                    </div>
                </Modal.Header>
                <Modal.Body>
                    <p className="bank-import-dialog__lead">
                        En tidigare hämtning kunde inte slutföras. Transaktioner kan fortfarande saknas,
                        även om den senaste uppdateringen lyckades.
                    </p>

                    {transactions.length > 0 && (
                        <section className="bank-import-dialog__section">
                            <h6>Berörda transaktioner ({count})</h6>
                            <div className="bank-import-dialog__table">
                                <table>
                                    <thead>
                                        <tr>
                                            <th>Datum</th>
                                            <th>Beskrivning</th>
                                            <th className="num">Belopp</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {transactions.map((transaction, index) => (
                                            <tr key={index}>
                                                <td className="date">{transaction.date || 'Datum saknas'}</td>
                                                <td>{transaction.description || 'Uppgifter saknas'}</td>
                                                <td className="num">
                                                    {transaction.amount !== null && transaction.amount !== undefined
                                                        ? `${formatAmount(transaction.amount)} ${account.currency || ''}`.trim()
                                                        : '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            {count > transactions.length && (
                                <p className="bank-import-dialog__hint">Visar de första {transactions.length} av {count}.</p>
                            )}
                        </section>
                    )}

                    <section className="bank-import-dialog__section">
                        <h6>Så löser du det</h6>
                        <ol className="bank-import-dialog__steps">
                            <li>
                                <strong>Försök hämta historiken igen.</strong>{' '}
                                Banken kan kräva en ny BankID-anslutning för att lämna ut äldre historik.
                            </li>
                            <li>
                                <strong>Eller importera från banken.</strong>{' '}
                                Exportera alla transaktioner på de berörda datumen, inklusive saldo, som CSV
                                och importera dem under Bankhändelser.
                            </li>
                        </ol>
                    </section>
                </Modal.Body>
                <Modal.Footer>
                    <Link to={`/${companyId}/bankstatements`} className="ll-btn ll-btn--secondary">
                        Importera CSV
                    </Link>
                    <button type="button" className="ll-btn ll-btn--primary" onClick={handleRetry} disabled={retryDisabled}>
                        Försök hämta historiken igen
                    </button>
                </Modal.Footer>
            </Modal>
        </>
    );
}
